"use client";

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
}

interface SegmentedControlProps<T extends string> {
  label: string;
  options: readonly SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
}

export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
}: SegmentedControlProps<T>) {
  return (
    <div>
      <p className="text-muted-foreground mb-1.5 text-[10px] font-medium tracking-[0.18em] uppercase">
        {label}
      </p>
      <div
        role="group"
        aria-label={label}
        className="border-border bg-surface-2/60 grid auto-cols-fr grid-flow-col gap-1 rounded-lg border p-1"
      >
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            disabled={option.disabled}
            aria-pressed={option.value === value}
            onClick={() => onChange(option.value)}
            className={cn(
              "rounded-md px-2 py-1.5 text-xs font-medium transition-colors",
              option.value === value
                ? "bg-foreground text-background"
                : "text-muted-foreground hover:text-foreground",
              option.disabled && "hover:text-muted-foreground cursor-not-allowed opacity-35"
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

const SIZE_MIN = 16;
const SIZE_MAX = 4096;

interface NumberFieldProps {
  label: string;
  value: number;
  onCommit: (value: number) => void;
  min?: number;
  max?: number;
}

/** Numeric px input — free typing, clamped and committed on blur or Enter. */
export function NumberField({
  label,
  value,
  onCommit,
  min = SIZE_MIN,
  max = SIZE_MAX,
}: NumberFieldProps) {
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const commit = () => {
    const parsed = Math.round(Number(draft));
    if (Number.isFinite(parsed)) {
      const clamped = Math.min(max, Math.max(min, parsed));
      setDraft(String(clamped));
      onCommit(clamped);
    } else {
      setDraft(String(value));
    }
  };

  return (
    <label className="border-border bg-surface-2/60 focus-within:border-foreground/30 flex min-w-0 flex-1 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 transition-colors">
      <span className="text-muted-foreground text-[10px] font-medium tracking-[0.14em] uppercase">
        {label}
      </span>
      <input
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
        }}
        className="text-foreground w-full min-w-0 [appearance:textfield] bg-transparent text-right font-mono text-xs tabular-nums outline-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        aria-label={`${label} in pixels`}
      />
      <span className="text-muted-foreground text-[10px]">px</span>
    </label>
  );
}

interface SizeFieldProps {
  label: string;
  value: number;
  presets: readonly number[];
  onChange: (value: number) => void;
}

/** Preset size chips plus a free custom px input. */
export function SizeField({ label, value, presets, onChange }: SizeFieldProps) {
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <p className="text-muted-foreground text-[10px] font-medium tracking-[0.18em] uppercase">
          {label}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {presets.map((preset) => (
          <button
            key={preset}
            type="button"
            aria-pressed={preset === value}
            onClick={() => onChange(preset)}
            className={cn(
              "rounded-md border px-2 py-1 font-mono text-[11px] tabular-nums transition-colors",
              preset === value
                ? "border-foreground bg-foreground text-background"
                : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
            )}
          >
            {preset}
          </button>
        ))}
      </div>
      <div className="mt-2">
        <NumberField label="Custom" value={value} onCommit={onChange} />
      </div>
    </div>
  );
}

interface ToggleRowProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export function ToggleRow({ label, checked, onChange }: ToggleRowProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="group flex w-full items-center justify-between gap-3 text-left"
    >
      <span className="text-foreground block min-w-0 text-sm font-medium">{label}</span>
      <span
        aria-hidden
        className={cn(
          "relative h-5 w-9 shrink-0 rounded-full transition-colors",
          checked ? "bg-brand-green" : "bg-surface-2 border-border border"
        )}
      >
        <span
          className={cn(
            "absolute top-1/2 left-0.5 h-4 w-4 -translate-y-1/2 rounded-full bg-white shadow-sm transition-transform",
            checked && "translate-x-4"
          )}
        />
      </span>
    </button>
  );
}
